import { getDomain, getEnvironment, isDev } from '@library/environment/isomorphic'
import { composeFetch, type RequestConfig } from '@library/http'
import {
  addPath,
  composeRequest,
  goustoHeaders,
  setHeader,
  setHost,
  setMethod,
} from '@library/http/requests'

import type {
  ApplyDashboardPromotionCodeRequest,
  ApplyDashboardPromotionCodeResponse,
  DashboardExperienceActionResponse,
  DashboardExperienceDownloadAppResponse,
  DashboardExperienceOrderAdditionResponse,
  DashboardExperienceOrderDeliveryDetailsResponse,
  DashboardExperienceOrderNewAndLimitedResponse,
  DashboardExperienceOrderRecipeCarouselSectionItem,
  DashboardExperienceOrderRecipeHighlightsSectionItem,
  DashboardExperienceOrderResponse,
  DashboardExperienceOrderSkipDiscountResponse,
  DashboardExperienceOrderTopPicksResponse,
  DashboardExperiencePageTitleResponse,
  DashboardExperienceResponse,
  DashboardExperienceRootSectionItem,
  DashboardExperienceSectionItem,
  DashboardExperienceV2OrderResponse,
  DashboardExperienceV2Response,
  DashboardExperienceRafCampaignResponse,
  DashboardExperienceRafStatusResponse,
  UpdateDashboardExperienceOrderRequest,
} from './interfaces'

const LOCAL_API_HOST = 'https://staging-api.gousto.info'
const DAY_IN_MS = 24 * 60 * 60 * 1000
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/
const DELIVERY_TITLE_PATTERN =
  /^(Sunday|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday)\s+(\d{1,2})(?:st|nd|rd|th)\s+([A-Za-z]+)(?:\s+\d{4})?$/i
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTHS_BY_NAME: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
}

function getActivateApiHost() {
  if (isDev()) {
    return LOCAL_API_HOST
  }

  return `https://${getEnvironment()}-api.${getDomain()}`
}

const normaliseAvailableDeliveryDays = (
  availableDeliveryDays: DashboardExperienceOrderDeliveryDetailsResponse['available_delivery_days'],
): DashboardExperienceOrderDeliveryDetailsResponse['available_delivery_days'] => {
  if (!availableDeliveryDays) {
    return availableDeliveryDays
  }

  return Object.fromEntries(
    Object.entries(availableDeliveryDays).map(([date, deliveryDay]) => {
      const {
        is_default: snakeCaseIsDefault,
        core_day_id: snakeCaseCoreDayId,
        unavailable_reason: snakeCaseUnavailableReason,
        alternate_delivery_day: snakeCaseAlternateDeliveryDay,
        slots,
        ...restDeliveryDay
      } = deliveryDay as typeof deliveryDay & {
        is_default?: boolean
        core_day_id?: string
        unavailable_reason?: string
        alternate_delivery_day?: string | null
      }

      return [
        date,
        {
          ...restDeliveryDay,
          isDefault: deliveryDay.isDefault ?? snakeCaseIsDefault ?? false,
          coreDayId: deliveryDay.coreDayId ?? snakeCaseCoreDayId ?? '',
          unavailableReason: deliveryDay.unavailableReason ?? snakeCaseUnavailableReason ?? '',
          alternateDeliveryDay:
            deliveryDay.alternateDeliveryDay ?? snakeCaseAlternateDeliveryDay ?? null,
          slots: slots.map((slot) => {
            const {
              day_slot_lead_time_id: snakeCaseDaySlotLeadTimeId,
              core_slot_id: snakeCaseCoreSlotId,
              delivery_start_time: snakeCaseDeliveryStartTime,
              delivery_end_time: snakeCaseDeliveryEndTime,
              delivery_price: snakeCaseDeliveryPrice,
              when_cutoff: snakeCaseWhenCutoff,
              default_day: snakeCaseDefaultDay,
              day_slot_lead_time_is_express: snakeCaseDaySlotLeadTimeIsExpress,
              is_default: snakeCaseSlotIsDefault,
              cutoff_day: snakeCaseCutoffDay,
              cutoff_time: snakeCaseCutoffTime,
              ...restSlot
            } = slot as typeof slot & {
              day_slot_lead_time_id?: string
              core_slot_id?: string
              delivery_start_time?: string
              delivery_end_time?: string
              delivery_price?: string
              when_cutoff?: string
              default_day?: number
              day_slot_lead_time_is_express?: boolean
              is_default?: boolean
              cutoff_day?: number
              cutoff_time?: string
            }

            return {
              ...restSlot,
              daySlotLeadTimeId: slot.daySlotLeadTimeId ?? snakeCaseDaySlotLeadTimeId ?? '',
              coreSlotId: slot.coreSlotId ?? snakeCaseCoreSlotId ?? '',
              deliveryStartTime: slot.deliveryStartTime ?? snakeCaseDeliveryStartTime ?? '',
              deliveryEndTime: slot.deliveryEndTime ?? snakeCaseDeliveryEndTime ?? '',
              deliveryPrice: slot.deliveryPrice ?? snakeCaseDeliveryPrice ?? '',
              whenCutoff: slot.whenCutoff ?? snakeCaseWhenCutoff ?? '',
              defaultDay: slot.defaultDay ?? snakeCaseDefaultDay ?? 0,
              daySlotLeadTimeIsExpress:
                slot.daySlotLeadTimeIsExpress ?? snakeCaseDaySlotLeadTimeIsExpress ?? false,
              isDefault: slot.isDefault ?? snakeCaseSlotIsDefault ?? false,
              cutoffDay: slot.cutoffDay ?? snakeCaseCutoffDay ?? 0,
              cutoffTime: slot.cutoffTime ?? snakeCaseCutoffTime ?? '',
            }
          }),
        },
      ]
    }),
  )
}

const getDateKey = (date: Date): string => {
  const year = date.getUTCFullYear().toString().padStart(4, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const day = date.getUTCDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

const parseIsoDate = (date: string): Date | null => {
  const match = date.match(ISO_DATE_PATTERN)

  if (!match) {
    return null
  }

  const [, year, month, day] = match
  const parsedDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

  if (
    parsedDate.getUTCFullYear() !== Number(year) ||
    parsedDate.getUTCMonth() !== Number(month) - 1 ||
    parsedDate.getUTCDate() !== Number(day)
  ) {
    return null
  }

  return parsedDate
}

const getDeliveryDateFromTitle = (title: string, analyticsDeliveryDate: string): string | null => {
  const analyticsDate = parseIsoDate(analyticsDeliveryDate)
  const titleMatch = title.trim().match(DELIVERY_TITLE_PATTERN)

  if (!analyticsDate || !titleMatch) {
    return null
  }

  const [, weekdayName, day, monthName] = titleMatch
  const month = MONTHS_BY_NAME[monthName.toLowerCase()]

  if (!month) {
    return null
  }

  const analyticsYear = analyticsDate.getUTCFullYear()
  const candidateYears = [analyticsYear - 1, analyticsYear, analyticsYear + 1]

  for (const candidateYear of candidateYears) {
    const candidateDate = new Date(Date.UTC(candidateYear, month - 1, Number(day)))

    if (
      candidateDate.getUTCFullYear() !== candidateYear ||
      candidateDate.getUTCMonth() !== month - 1 ||
      candidateDate.getUTCDate() !== Number(day)
    ) {
      continue
    }

    const candidateWeekday = WEEKDAY_NAMES[candidateDate.getUTCDay()]

    if (candidateWeekday.toLowerCase() !== weekdayName.toLowerCase()) {
      continue
    }

    if (Math.abs(candidateDate.getTime() - analyticsDate.getTime()) <= DAY_IN_MS) {
      return getDateKey(candidateDate)
    }
  }

  return null
}

// The dashboard API can report the analytics date one UTC day behind the user-facing title.
const normaliseOrderAnalyticsDeliveryDate = (
  order: DashboardExperienceOrderResponse,
): DashboardExperienceOrderResponse => {
  const deliveryDateFromTitle = getDeliveryDateFromTitle(order.title, order.analytics.delivery_date)

  if (!deliveryDateFromTitle || deliveryDateFromTitle === order.analytics.delivery_date) {
    return order
  }

  return {
    ...order,
    analytics: {
      ...order.analytics,
      delivery_date: deliveryDateFromTitle,
    },
  }
}

const normaliseDashboardExperience = (
  dashboardExperience: DashboardExperienceResponse,
): DashboardExperienceResponse => {
  if (!dashboardExperience.orders) {
    return dashboardExperience
  }

  return {
    ...dashboardExperience,
    orders: dashboardExperience.orders.map(normaliseOrderAnalyticsDeliveryDate),
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isString = (value: unknown): value is string => typeof value === 'string'

const isNumber = (value: unknown): value is number => typeof value === 'number'

const isPageTitleResponse = (data: unknown): data is DashboardExperiencePageTitleResponse =>
  isRecord(data) && isString(data.text)

const isDashboardExperienceActionResponse = (
  data: unknown,
): data is DashboardExperienceActionResponse =>
  isRecord(data) && isString(data.title) && isString(data.type)

const isDashboardExperienceDownloadAppResponse = (
  data: unknown,
): data is DashboardExperienceDownloadAppResponse => {
  if (!isRecord(data) || !isString(data.title) || !Array.isArray(data.bullets)) {
    return false
  }

  if (
    !data.bullets.every(
      (bullet) => isRecord(bullet) && isString(bullet.icon) && isString(bullet.title),
    )
  ) {
    return false
  }

  return isRecord(data.stars) && isNumber(data.stars.rating) && isString(data.stars.value)
}

const isDashboardExperienceRafCampaignResponse = (
  data: unknown,
): data is DashboardExperienceRafCampaignResponse =>
  isRecord(data) &&
  isString(data.title) &&
  isString(data.subtitle) &&
  isString(data.code) &&
  Array.isArray(data.tiers) &&
  data.tiers.every((tier) => isRecord(tier) && isString(tier.amount) && isString(tier.name)) &&
  isString(data.send_cta)

const isMetric = (data: unknown): data is { title: string; value: string } =>
  isRecord(data) && isString(data.title) && isString(data.value)

const isDashboardExperienceRafStatusResponse = (
  data: unknown,
): data is DashboardExperienceRafStatusResponse =>
  isRecord(data) &&
  isString(data.title) &&
  isString(data.subtitle) &&
  isMetric(data.referralCount) &&
  isMetric(data.referralCredit) &&
  isString(data.invite_cta)

const isDashboardExperienceOrderTopPicksResponse = (
  data: unknown,
): data is DashboardExperienceOrderTopPicksResponse =>
  isRecord(data) && isString(data.title) && Array.isArray(data.recipes)

const isDashboardExperienceOrderNewAndLimitedResponse = (
  data: unknown,
): data is DashboardExperienceOrderNewAndLimitedResponse =>
  isRecord(data) &&
  isString(data.title) &&
  isString(data.subtitle) &&
  isString(data.see_more_label) &&
  Array.isArray(data.recipes)

const getFirstSectionData = <TData>(
  items: DashboardExperienceSectionItem[],
  type: string,
): TData | undefined => {
  const matchingItem = items.find((item) => item.type === type)

  if (!matchingItem || matchingItem.data === undefined) {
    return undefined
  }

  return matchingItem.data as TData
}

const getSectionTitle = (data: unknown): string => {
  if (typeof data !== 'object' || data === null || !('title' in data)) {
    return ''
  }

  const { title } = data as { title?: unknown }

  return typeof title === 'string' ? title : ''
}

const toOrderAddition = (
  item: DashboardExperienceSectionItem,
): DashboardExperienceOrderAdditionResponse | null => {
  if (item.type === 'recipe_highlights' && isDashboardExperienceOrderTopPicksResponse(item.data)) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  if (
    item.type === 'recipe_carousel' &&
    isDashboardExperienceOrderNewAndLimitedResponse(item.data)
  ) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  return null
}

const isRecipeHighlightsAddition = (
  addition: DashboardExperienceOrderAdditionResponse,
): addition is DashboardExperienceOrderRecipeHighlightsSectionItem =>
  addition.type === 'recipe_highlights'

const isRecipeCarouselAddition = (
  addition: DashboardExperienceOrderAdditionResponse,
): addition is DashboardExperienceOrderRecipeCarouselSectionItem =>
  addition.type === 'recipe_carousel'

const normaliseDashboardExperienceV2Order = (
  order: DashboardExperienceV2OrderResponse,
): DashboardExperienceOrderResponse => {
  const orderAdditions = (order.additions ?? [])
    .map(toOrderAddition)
    .filter((addition): addition is DashboardExperienceOrderAdditionResponse => Boolean(addition))
  const topPicks = orderAdditions.find(isRecipeHighlightsAddition)?.data ?? order.top_picks
  const newAndLimited = orderAdditions.find(isRecipeCarouselAddition)?.data ?? order.new_and_limited
  const {
    additions: _additions,
    new_and_limited: _newAndLimited,
    top_picks: _topPicks,
    ...orderWithoutAdditions
  } = order

  return {
    ...orderWithoutAdditions,
    top_picks: topPicks ?? null,
    new_and_limited: newAndLimited ?? null,
    ...(orderAdditions.length > 0 ? { additions: orderAdditions } : {}),
  }
}

const toSupportedRootSectionItem = (
  item: DashboardExperienceSectionItem,
): DashboardExperienceRootSectionItem | null => {
  if (item.type === 'page_title' && isPageTitleResponse(item.data)) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  if (item.type === 'action' && isDashboardExperienceActionResponse(item.data)) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  if (item.type === 'app_download_prompt' && isDashboardExperienceDownloadAppResponse(item.data)) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  if (item.type === 'raf_progress_banner' && isDashboardExperienceRafStatusResponse(item.data)) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  if (item.type === 'raf_campaign_banner' && isDashboardExperienceRafCampaignResponse(item.data)) {
    return {
      type: item.type,
      data: item.data,
    }
  }

  return null
}

const toSupportedRootSectionItems = (
  items: DashboardExperienceSectionItem[],
): DashboardExperienceRootSectionItem[] =>
  items
    .map(toSupportedRootSectionItem)
    .filter((item): item is DashboardExperienceRootSectionItem => Boolean(item))

const toDashboardExperienceAction = (
  item: DashboardExperienceRootSectionItem,
): DashboardExperienceActionResponse | null => {
  if (item.type === 'action') {
    return item.data
  }

  if (item.type === 'app_download_prompt') {
    return {
      title: getSectionTitle(item.data),
      type: 'downloadApp',
    }
  }

  if (item.type === 'raf_progress_banner' || item.type === 'raf_campaign_banner') {
    return {
      title: getSectionTitle(item.data),
      type: 'raf',
    }
  }

  return null
}

const normaliseDashboardExperienceV2 = (
  dashboardExperience: DashboardExperienceV2Response,
): DashboardExperienceResponse => {
  const header = toSupportedRootSectionItems(dashboardExperience.header ?? [])
  const globalOrdersAdditions = toSupportedRootSectionItems(
    dashboardExperience.global_orders_additions ?? [],
  )
  const footer = toSupportedRootSectionItems(dashboardExperience.footer ?? [])
  const rootItems = [...header, ...globalOrdersAdditions, ...footer]
  const pageTitle =
    getFirstSectionData<DashboardExperiencePageTitleResponse>(rootItems, 'page_title')?.text ?? ''
  const actions = rootItems
    .map(toDashboardExperienceAction)
    .filter((action): action is DashboardExperienceActionResponse => Boolean(action))
  const downloadApp = getFirstSectionData<DashboardExperienceDownloadAppResponse>(
    rootItems,
    'app_download_prompt',
  )
  const rafStatus = getFirstSectionData<DashboardExperienceRafStatusResponse>(
    rootItems,
    'raf_progress_banner',
  )
  const rafCampaign = getFirstSectionData<DashboardExperienceRafCampaignResponse>(
    rootItems,
    'raf_campaign_banner',
  )

  return normaliseDashboardExperience({
    page_title: pageTitle,
    order_tabs: dashboardExperience.order_tabs ?? [],
    orders: (dashboardExperience.orders ?? []).map(normaliseDashboardExperienceV2Order),
    actions,
    ...(downloadApp !== undefined ? { downloadApp } : {}),
    ...(rafStatus !== undefined ? { rafStatus } : {}),
    ...(rafCampaign !== undefined ? { rafCampaign } : {}),
    sections: {
      header,
      global_orders_additions: globalOrdersAdditions,
      footer,
    },
  })
}

const parseDashboardExperience = async (
  response: Response,
): Promise<DashboardExperienceResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const dashboardExperience = (await response.json()) as DashboardExperienceResponse

  return normaliseDashboardExperience(dashboardExperience)
}

const parseDashboardExperienceV2 = async (
  response: Response,
): Promise<DashboardExperienceResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const dashboardExperience = (await response.json()) as DashboardExperienceV2Response

  return normaliseDashboardExperienceV2(dashboardExperience)
}

const parseOrderDeliveryDetails = async (
  response: Response,
): Promise<DashboardExperienceOrderDeliveryDetailsResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const orderDeliveryDetails =
    (await response.json()) as DashboardExperienceOrderDeliveryDetailsResponse

  return {
    ...orderDeliveryDetails,
    available_delivery_days: normaliseAvailableDeliveryDays(
      orderDeliveryDetails.available_delivery_days,
    ),
  }
}

const parseUpdateDashboardExperienceOrder = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()

    if (
      response.status === 500 &&
      (responseMessage.trim() === '' || responseMessage.trim() === 'Internal Server Error')
    ) {
      return null
    }

    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

const parseSkipOrderResponse = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

const isOrderSkipDiscountResponse = (
  responseBody: unknown,
): responseBody is DashboardExperienceOrderSkipDiscountResponse => {
  if (typeof responseBody !== 'object' || responseBody === null || Array.isArray(responseBody)) {
    return false
  }

  const skipDiscount = responseBody as Record<string, unknown>

  return (
    typeof skipDiscount.title === 'string' &&
    typeof skipDiscount.subtitle === 'string' &&
    typeof skipDiscount.discount === 'string' &&
    typeof skipDiscount.applyCta === 'string' &&
    typeof skipDiscount.skipCta === 'string' &&
    typeof skipDiscount.isExistingDiscount === 'boolean' &&
    (typeof skipDiscount.promoCode === 'string' || skipDiscount.promoCode === null)
  )
}

const parseOrderSkipDiscountResponse = async (
  response: Response,
): Promise<DashboardExperienceOrderSkipDiscountResponse | null> => {
  if (response.status === 204) {
    return null
  }

  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  try {
    const responseBody = (await response.json()) as unknown

    return isOrderSkipDiscountResponse(responseBody) ? responseBody : null
  } catch {
    return null
  }
}

const isApplyDashboardPromotionCodeResponse = (
  responseBody: unknown,
): responseBody is ApplyDashboardPromotionCodeResponse => {
  if (typeof responseBody !== 'object' || responseBody === null || Array.isArray(responseBody)) {
    return false
  }

  const applyPromotionCodeResponse = responseBody as Record<string, unknown>

  return (
    typeof applyPromotionCodeResponse.promoCode === 'string' &&
    (applyPromotionCodeResponse.status === 'applied' ||
      applyPromotionCodeResponse.status === 'alreadyApplied')
  )
}

const parseApplyDashboardPromotionCodeResponse = async (
  response: Response,
): Promise<ApplyDashboardPromotionCodeResponse> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  const responseBody = (await response.json()) as unknown

  if (!isApplyDashboardPromotionCodeResponse(responseBody)) {
    throw new Error('service responded with invalid apply promotion code response')
  }

  return responseBody
}

const parseRestoreOrderResponse = async (response: Response): Promise<null> => {
  if (!response.ok) {
    const responseMessage = await response.text()
    throw new Error(`service responded with ${response.status}: ${responseMessage}`)
  }

  return null
}

export const fetchDashboardExperience = composeFetch(
  composeRequest(setHost(getActivateApiHost), addPath('activate/dashboard/v1'), goustoHeaders),
  parseDashboardExperience,
)

export const fetchDashboardExperienceV2 = composeFetch(
  composeRequest(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/v2'),
    goustoHeaders,
  ),
  parseDashboardExperienceV2,
)

export const fetchOrderDeliveryDetails = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/order_delivery_details/v1'),
    addPath((orderId) => orderId),
    goustoHeaders,
  ),
  parseOrderDeliveryDetails,
)

export const updateDashboardExperienceOrder = composeFetch(
  composeRequest<{ orderId: string; body: UpdateDashboardExperienceOrderRequest }>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/update-order/v1'),
    addPath(({ orderId }) => orderId),
    setHeader('Content-Type', 'application/json'),
    setMethod('PUT'),
    (
      request: RequestConfig,
      input: { orderId: string; body: UpdateDashboardExperienceOrderRequest },
    ) => {
      const requestBody = JSON.stringify(input.body)

      request.body = requestBody

      if (isDev()) {
        // Surface the exact request payload in local development when the backend returns opaque 500s.
        // eslint-disable-next-line no-console
        console.info('updateDashboardExperienceOrder request body', JSON.parse(requestBody))
      }

      return request
    },
    goustoHeaders,
  ),
  parseUpdateDashboardExperienceOrder,
)

export const skipOrder = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/skip_order/v1'),
    addPath((orderId) => orderId),
    setMethod('DELETE'),
    goustoHeaders,
  ),
  parseSkipOrderResponse,
)

export const fetchOrderSkipDiscount = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/order_skip_discount/v1'),
    addPath((orderId) => orderId),
    goustoHeaders,
  ),
  parseOrderSkipDiscountResponse,
)

export const applyDashboardPromotionCode = composeFetch(
  composeRequest<ApplyDashboardPromotionCodeRequest>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/apply_promotion_code/v1'),
    setHeader('Content-Type', 'application/json'),
    setMethod('POST'),
    (request: RequestConfig, input: ApplyDashboardPromotionCodeRequest) => {
      request.body = JSON.stringify(input)

      return request
    },
    goustoHeaders,
  ),
  parseApplyDashboardPromotionCodeResponse,
)

export const restoreOrder = composeFetch(
  composeRequest<string>(
    setHost(getActivateApiHost),
    addPath('activate/customerengagement/dashboard/restore_order/v1'),
    addPath((orderId) => orderId),
    setMethod('POST'),
    goustoHeaders,
  ),
  parseRestoreOrderResponse,
)
